/** Food perspective routes, tools, layers, and fallback mock data. */

import { FOOD_FRESH_FOOD_LAYER_OPTIONS } from "./foodAccessPointCategories.js";

export const FOOD_BASE_PATH = "/food";

export const FOOD_ACCESS_STATUSES = Object.freeze({
  GOOD_ACCESS: "GOOD_ACCESS",
  MODERATE_ACCESS: "MODERATE_ACCESS",
  LOW_ACCESS: "LOW_ACCESS",
  NO_ACCESS: "NO_ACCESS",
  INSUFFICIENT_DATA: "INSUFFICIENT_DATA",
});

export const FOOD_SECTIONS = {
  DASHBOARD: "dashboard",
  MAP: "map",
  COMPARE: "compare",
  BASIC_BASKET: "basic-basket",
  WHOLESALE: "wholesale-markets",
  SUPPLY_SIGNALS: "supply-signals",
  INPUT_SIGNALS: "input-signals",
  FOOD_ACCESS: "food-access",
  WATCHLISTS: "watchlists",
  ALERTS: "alerts",
  INTELLIGENCE: "intelligence",
  SOURCES_HEALTH: "sources-health",
};

export const FOOD_SECTION_PATHS = {
  [FOOD_SECTIONS.DASHBOARD]: FOOD_BASE_PATH,
  [FOOD_SECTIONS.MAP]: `${FOOD_BASE_PATH}/food-access`,
  [FOOD_SECTIONS.COMPARE]: `${FOOD_BASE_PATH}/compare`,
  [FOOD_SECTIONS.BASIC_BASKET]: `${FOOD_BASE_PATH}/basic-basket`,
  [FOOD_SECTIONS.WHOLESALE]: `${FOOD_BASE_PATH}/wholesale-markets`,
  [FOOD_SECTIONS.SUPPLY_SIGNALS]: `${FOOD_BASE_PATH}/supply-signals`,
  [FOOD_SECTIONS.INPUT_SIGNALS]: `${FOOD_BASE_PATH}/input-signals`,
  [FOOD_SECTIONS.FOOD_ACCESS]: `${FOOD_BASE_PATH}/food-access`,
  [FOOD_SECTIONS.WATCHLISTS]: `${FOOD_BASE_PATH}/watchlists`,
  [FOOD_SECTIONS.ALERTS]: `${FOOD_BASE_PATH}/alerts`,
  [FOOD_SECTIONS.INTELLIGENCE]: `${FOOD_BASE_PATH}/intelligence`,
  [FOOD_SECTIONS.SOURCES_HEALTH]: `${FOOD_BASE_PATH}/sources-health`,
};

export const FOOD_TOOLS = [
  {
    id: FOOD_SECTIONS.DASHBOARD,
    icon: "📊",
    label: "Price Dashboard",
    desc: "Basket totals, movers, retail pulse, upstream context",
  },
  {
    id: FOOD_SECTIONS.COMPARE,
    icon: "🛒",
    label: "Compare Supermarkets",
    desc: "Rank baskets, compare staples, and spot spreads",
  },
  {
    id: FOOD_SECTIONS.BASIC_BASKET,
    icon: "🧺",
    label: "Basic Basket",
    desc: "Coverage, staple costs, and basket history",
  },
  {
    id: FOOD_SECTIONS.WHOLESALE,
    icon: "🏭",
    label: "Wholesale Markets",
    desc: "DANE SIPSA wholesale prices vs retail",
  },
  {
    id: FOOD_SECTIONS.SUPPLY_SIGNALS,
    icon: "📦",
    label: "Supply Signals",
    desc: "SIPSA abastecimiento volume & pressure",
  },
  {
    id: FOOD_SECTIONS.INPUT_SIGNALS,
    icon: "🌾",
    label: "Input Signals",
    desc: "Farm & production input cost pressure",
  },
  {
    id: FOOD_SECTIONS.FOOD_ACCESS,
    icon: "🗺",
    label: "Food Access Map",
    desc: "Affordability and access overlays",
  },
  {
    id: FOOD_SECTIONS.WATCHLISTS,
    icon: "👁",
    label: "Watchlists",
    desc: "Monitor products, baskets, stores, and areas",
  },
  {
    id: FOOD_SECTIONS.ALERTS,
    icon: "🔔",
    label: "Alerts",
    desc: "Price, basket, supply, and access notifications",
  },
  {
    id: FOOD_SECTIONS.INTELLIGENCE,
    icon: "💡",
    label: "Food Intelligence",
    desc: "Explainable buy, watch, and access recommendations",
  },
  {
    id: FOOD_SECTIONS.SOURCES_HEALTH,
    icon: "🔗",
    label: "Sources & Health",
    desc: "Supermarket source registry status",
  },
];

export const FOOD_LAYER_OPTIONS = [
  {
    id: "foodStores",
    label: "Food Stores",
    desc: "All mapped food retail locations",
    defaultVisible: false,
  },
  {
    id: "foodSupermarkets",
    label: "Supermarkets",
    desc: "Major supermarket chains",
    defaultVisible: true,
  },
  {
    id: "foodSupermarketBuffers",
    label: "Supermarket 500 m buffers",
    desc: "Straight-line catchment around each mapped supermarket (baseline)",
    defaultVisible: true,
  },
  {
    id: "foodWalkingIsochrones",
    label: "Walking access zones",
    desc: "Estimated walking access from supermarkets (optional; 5–20 min bands)",
    defaultVisible: false,
  },
  {
    id: "foodDiscountStores",
    label: "Discount Stores",
    desc: "D1, Ara, and other budget chains",
    defaultVisible: false,
  },
  {
    id: "foodBasicBasketAffordability",
    label: "Basket Affordability",
    desc: "Stores with latest basic basket cost",
    defaultVisible: false,
  },
  {
    id: "foodAccessGaps",
    label: "Food Access Areas",
    desc: "Neighborhood access and affordability scores",
    defaultVisible: false,
  },
  {
    id: "foodWholesaleMarkets",
    label: "Wholesale Markets",
    desc: "SIPSA wholesale market locations",
    defaultVisible: false,
  },
  {
    id: "foodSupplyPressure",
    label: "Supply Pressure",
    desc: "Supply volume pressure indicators",
    defaultVisible: false,
  },
  {
    id: "foodInputPressure",
    label: "Input Pressure",
    desc: "Farm and production input cost pressure",
    defaultVisible: false,
  },
  ...FOOD_FRESH_FOOD_LAYER_OPTIONS.map((option) => ({
    id: option.id,
    label: option.label,
    desc: option.desc,
    defaultVisible: option.defaultVisible,
    adminOnly: option.adminOnly,
  })),
];

export const FOOD_FALLBACK_DISCOVERY_SUMMARY = {
  total: 6,
  publicPriceConfirmed: 0,
  needsManualReview: 0,
  notStarted: 6,
  blockedOrDoNotIngest: 0,
  inReview: 0,
  nextRecommendedSourceKey: "EXITO_SUPERMARKET",
  nextRecommendedSourceName: "Éxito",
};

export const FOOD_FALLBACK_SUMMARY = {
  basicBasketCostCop: 428500,
  basicBasketCostFormatted: "COP 428.500",
  cheapestStore: "Éxito",
  biggestPriceIncrease: { product: "Tomato", changePct: 14 },
  biggestPriceDecrease: { product: "Potato", changePct: -8 },
  dataFreshnessNote: "Mock data, not yet connected",
  setupNotice:
    "Food price monitoring is currently in setup mode. Retail supermarket ingestion has not collected observations yet.",
  setupMode: true,
  discoverySummary: FOOD_FALLBACK_DISCOVERY_SUMMARY,
};

export const FOOD_SECTION_PLACEHOLDERS = {
  [FOOD_SECTIONS.COMPARE]: {
    title: "Compare Supermarkets",
    body: "Compare ARA and Éxito basic basket totals, coverage, staple gaps, and item-level prices.",
  },
  [FOOD_SECTIONS.BASIC_BASKET]: {
    title: "Basic Colombian Household Basket",
    body: "Staple quantities for a four-person household — seeded in the database for future price rollups.",
  },
  [FOOD_SECTIONS.FOOD_ACCESS]: {
    title: "Food Access Map",
    body: "Map food stores, basket affordability, and neighborhood access scores across the Aburrá Valley.",
  },
  [FOOD_SECTIONS.SOURCES_HEALTH]: {
    title: "Sources & Health",
    body: "Supermarket chain sources are registered with discovery-pending health until ingestion is configured.",
  },
};

export function parseFoodSectionFromPath(pathname = "") {
  if (!pathname.startsWith(FOOD_BASE_PATH)) {
    return { section: null };
  }

  const remainder = pathname.slice(FOOD_BASE_PATH.length).replace(/^\//, "");
  if (!remainder) {
    return { section: FOOD_SECTIONS.DASHBOARD };
  }

  const segment = remainder.split("/")[0];
  if (segment === FOOD_SECTIONS.MAP) {
    return { section: FOOD_SECTIONS.FOOD_ACCESS };
  }
  const known = Object.values(FOOD_SECTIONS);
  return {
    section: known.includes(segment) ? segment : FOOD_SECTIONS.DASHBOARD,
  };
}

export function foodPathForSection(section) {
  return FOOD_SECTION_PATHS[section] || FOOD_BASE_PATH;
}

export function formatCop(amount) {
  if (amount == null || Number.isNaN(Number(amount))) {
    return "—";
  }

  return `COP ${Math.round(Number(amount)).toLocaleString("es-CO")}`;
}
